import React, { useCallback, useState } from 'react';

import _ from 'lodash';
import moment from 'moment';

import { Button, IconButton } from '@material-ui/core';
import { MdDelete, MdEdit, MdFileCopy, MdRemoveRedEye } from 'react-icons/md';

import API from 'utils/api';
import { BASE_URL } from 'consts';
import { useAuth, useSingleton, useSources, useTableStateRestore } from 'hooks';
import { ButtonContainer, ConfirmDialog, ErrorDialog, Table, Tooltip } from 'components';
import { getTemplateFriendlyType, getTemplateTypes } from 'templates';
import { textSort } from 'components/Table';
import { breakSentence, call } from 'utils';
import CreateDialog from './CreateDialog';
import Preview from '../Preview/Preview';

import styles from './TemplateList.module.scss';

const TemplateList = props => {
  const [dialog, setDialog] = useState({});

  const sources = useSources();
  const singleton = useSingleton();
  const auth = useAuth();

  const { tableProps, forwardLink } = useTableStateRestore(props);

  const fetchData = useCallback(
    ({ filter, ...rest }) =>
      API.getTemplates(
        {
          ...rest,
          query: filter
            ? JSON.stringify(
                _.mapValues(filter, (value, key) =>
                  key === 'type' ? value : { $regex: value, $options: 'i' }
                )
              )
            : undefined,
          select: 'name type updatedAt params.category',
          icon: true,
          team: true,
          league: true,
        },
        sources
      ),
    [sources]
  );

  const loadLeagues = useCallback(
    () =>
      API.getLeagues(
        { ignoreDependencies: true, select: 'name', sort: 'name' },
        sources
      ).then(parseOptions),
    [sources]
  );

  const loadTeams = useCallback(
    params => {
      const leagues = _.get(params, 'params.league');

      return leagues
        ? API.getTeams(
            {
              query: JSON.stringify({ leagues }),
              ignoreDependencies: true,
              select: 'name',
              sort: 'name',
            },
            sources
          ).then(parseOptions)
        : [];
    },
    [sources]
  );

  const renderActions = ({ _id: id, params, type }) => {
    const button = (Icon, onClick, title) => (
      <IconButton color="primary" onClick={onClick}>
        <Tooltip title={title} placement="top">
          <Icon />
        </Tooltip>
      </IconButton>
    );

    return (
      <div>
        {button(
          MdRemoveRedEye,
          () => setDialog({ preview: { id, type } }),
          'Preview this template'
        )}

        {button(
          MdEdit,
          () => props.history.push(forwardLink(`editor/${id}`)),
          'Edit this template'
        )}

        {button(
          MdFileCopy,
          () =>
            setDialog({
              clone: {
                id,
                message: [
                  'Are you sure you want to clone the following template?',
                  `"${params.category}"`,
                ],
              },
            }),

          'Clone this template'
        )}

        {button(
          MdDelete,
          () =>
            setDialog({
              delete: {
                id,
                message: [
                  'Are you sure you want to delete the following template?',
                  `"${params.category}"`,
                ],
              },
            }),
          'Delete this template'
        )}
      </div>
    );
  };

  const renderName = ({ iconUrl: url, params = {} }) => (
    <div className={styles.name}>
      <div
        className={styles.icon}
        style={{ backgroundImage: url ? `url(${url})` : null }}
      />
      <div className={styles.text}>{params.category}</div>
    </div>
  );

  const onCloseDialog = () => setDialog({});

  return (
    <div className={styles.templateList}>
      <ButtonContainer position="top">
        <Button
          variant="contained"
          color="primary"
          onClick={() => setDialog({ create: true })}
        >
          create new template
        </Button>
      </ButtonContainer>

      <Table
        {...tableProps}
        data={fetchData}
        title="Template List"
        filtering
        pageSizeOptions={[5, 10, 20, 40]}
        pageSize={10}
        search={false}
        columns={[
          {
            title: 'Name',
            field: 'params.category',
            weight: 2,
            render: renderName,
          },
          { title: 'Display Title', field: 'name', weight: 2 },
          {
            title: 'Type',
            field: 'type',
            weight: 1.2,
            customSort: textSort('type', getTemplateFriendlyType),
            render: ({ type }) => getTemplateFriendlyType(type),
            filtering: { type: 'select', options: getTemplateTypes() },
          },
          {
            title: 'League',
            field: 'params.league',
            render: ({ leagueName }) => leagueName,
            filtering: { type: 'select', options: loadLeagues },
          },
          {
            title: 'Team',
            field: 'params.team',
            render: ({ teamName }) => teamName,
            filtering: { type: 'select', options: loadTeams, depends: 'params.league' },
          },
          {
            title: 'Last Modified',
            field: 'updatedAt',
            align: 'center',
            defaultSort: 'desc',
            initialSort: 'desc',
            filtering: false,
            render: ({ updatedAt }) => (
              <div>{moment(updatedAt).format('YYYY/MM/DD hh:mm A')}</div>
            ),
          },
          {
            title: 'Actions',
            sorting: false,
            align: 'center',
            render: renderActions,
            filtering: false,
          },
        ]}
      />

      <CreateDialog
        open={!!dialog.create}
        onClose={onCloseDialog}
        onSelect={type => props.history.push(forwardLink('editor', { type }))}
      />

      <ConfirmDialog
        open={!!dialog.delete}
        title="Delete Template"
        message={_.get(dialog, 'delete.message', '')}
        busy={_.get(dialog, 'delete.busy')}
        actions={_.get(dialog, 'delete.actions')}
        onClose={yes => {
          if (yes) {
            const data = dialog.delete;

            setDialog({
              delete: {
                ...data,
                message: 'Deleting template resources...',
                busy: true,
              },
            });

            singleton(() =>
              API.deleteTemplate(data.id, sources).then(({ error }) => {
                if (error) {
                  setDialog({
                    delete: {
                      ...data,
                      message: breakSentence(String(error), 55),
                      actions: 'close',
                    },
                  });
                } else {
                  call(tableProps.tableRef.current.reload);
                  onCloseDialog();
                }
              })
            );
          } else {
            onCloseDialog();
          }
        }}
      />

      <ConfirmDialog
        open={!!dialog.clone}
        title="Clone Template"
        message={_.get(dialog, 'clone.message', '')}
        busy={_.get(dialog, 'clone.busy')}
        onClose={yes => {
          if (yes) {
            setDialog({
              clone: {
                ...dialog.clone,
                message: 'Cloning template resources...',
                busy: true,
              },
            });

            singleton(() =>
              API.copyTemplate(dialog.clone.id, sources).then(({ data }) => {
                if (data) {
                  props.history.push(forwardLink(`editor/${data}`));
                } else {
                  setDialog({ error: 'Failed to clone template' });
                }
              })
            );
          } else {
            onCloseDialog();
          }
        }}
      />

      <Preview
        open={!!dialog.preview}
        onClose={onCloseDialog}
        url={buildPreviewURL(dialog.preview, auth)}
      />

      <ErrorDialog open={!!dialog.error} onClose={onCloseDialog} message={dialog.error} />
    </div>
  );
};

const parseOptions = ({ data }) =>
  (_.get(data, 'docs') || []).map(({ _id, name }) => ({ text: name, value: _id }));

const buildPreviewURL = (preview, auth) => {
  if (preview) {
    const { redirect, type, id } = preview;

    if (redirect) {
      return redirect;
    }

    if (auth && auth.user) {
      return `${BASE_URL}/template/${type}/preview/${id}/${auth.user._id}`;
    }
  }
};

export default TemplateList;
